'use client'

import { ChevronLeftIcon } from '@heroicons/react/24/outline'

import { Button } from '@src/components/Button/Button'
import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import { useTopNav } from '@src/components/TopNavV3/TopNavContext'
import { TopNavSubMenuTileSlider } from '@src/components/TopNavV3/TopNavMobile/TopNavSubMenuTileSlider'
import type { TopNavSubItem } from '@src/components/TopNavV3/TopNavProps'
import { useTrackSelectNavCta } from '@src/utils/analytics.util'
import { cn } from '@src/utils/tailwind'

export const TopNavMobileSubMenu = () => {
  const trackSelectNavCta = useTrackSelectNavCta()
  const { expandedMenu, setMobileSubExpanded, setExpanded } = useTopNav()

  const renderTitle = () => {
    return (
      <div className="flex h-14 items-center border-b border-white/10 p-4">
        <button
          onClick={() => {
            setMobileSubExpanded(false)
          }}
          className="flex items-center gap-2"
        >
          <ChevronLeftIcon className="size-5 text-bodi-green-bright-500" />
          <p className="font-lateral text-body-lg">{expandedMenu?.title}</p>
        </button>
      </div>
    )
  }

  const renderCtaMobile = () => {
    const content = (
      <Button className="w-full" size="medium">
        {expandedMenu?.ctaMobile?.title}
      </Button>
    )
    return (
      expandedMenu?.hasCtaMobile &&
      expandedMenu?.ctaMobile?.title && (
        <div className="fixed bottom-0 left-0 w-screen border-t border-bodi-gray-dark bg-bodi-black-text px-4 py-5">
          <div className="font-medium text-bodi-green-bright-500">
            {expandedMenu?.ctaMobile?.href ? (
              <LocalizedLink
                data-testid="menu-featured-item-mobile"
                onClick={() => {
                  trackSelectNavCta({ 'cta url': expandedMenu?.ctaMobile?.href })
                  setExpanded(false)
                }}
                href={expandedMenu?.ctaMobile?.href ?? ''}
                rel={expandedMenu?.ctaMobile?.newTab ? 'noopener noreferrer' : ''}
                target={expandedMenu?.ctaMobile?.newTab ? '_blank' : '_self'}
              >
                {content}
              </LocalizedLink>
            ) : (
              <div>{content}</div>
            )}
          </div>
        </div>
      )
    )
  }

  const renderSubItem = (item: TopNavSubItem, itemIdx: number) => {
    return (
      <div
        className={cn('border-t border-white/10 px-4 py-6', {
          'border-t-0': itemIdx === 0,
        })}
      >
        <div className="text-heading-5 mb-4">
          {item.href ? (
            <LocalizedLink
              className="inline-block hover:underline"
              data-testid={`sub-menu-${itemIdx}`}
              onClick={() => {
                trackSelectNavCta({ 'cta url': item?.href })
                setExpanded(false)
              }}
              href={item?.href}
              key={item?.title}
              rel={item?.newTab ? 'noopener noreferrer' : ''}
              target={item?.newTab ? '_blank' : '_self'}
            >
              {item.title}
            </LocalizedLink>
          ) : (
            <div>{item.title}</div>
          )}
        </div>

        <div className="flex flex-col">
          {item.links
            ?.filter((subItem) => Boolean(subItem.title))
            .map((subItem, subItemIdx) => (
              <LocalizedLink
                className="flex items-center gap-1 p-2 text-body-lg font-medium text-bodi-gray-butters hover:underline"
                data-testid={`sub-menu-${subItemIdx}`}
                onClick={() => {
                  trackSelectNavCta({ 'cta url': subItem?.href })
                  setExpanded(false)
                }}
                href={subItem?.href ?? ''}
                key={subItem?.title}
                rel={subItem?.newTab ? 'noopener noreferrer' : ''}
                target={subItem?.newTab ? '_blank' : '_self'}
              >
                <div>{subItem.title}</div>
                {subItem.stamp && (
                  <>
                    {'-'}
                    <span className="text-body-sm font-medium text-bodi-green-bright-500">
                      {subItem.stamp}
                    </span>
                  </>
                )}
              </LocalizedLink>
            ))}
        </div>
      </div>
    )
  }

  return (
    <div className="relative h-[calc(100%-85px)] overflow-y-scroll">
      {renderTitle()}

      <div className="relative flex flex-col">
        {expandedMenu?.subItems
          ?.filter((item) => Boolean(item.title))
          .map((item, itemIdx) => renderSubItem(item, itemIdx))}

        <TopNavSubMenuTileSlider />
        {renderCtaMobile()}
      </div>
    </div>
  )
}
